/**
 * PKCE PRIMITIVES. The three-line mistake this file exists to make impossible.
 * ===========================================================================
 *
 * RFC 7636 §4.2: `code_challenge = BASE64URL-ENCODE(SHA256(ASCII(code_verifier)))`. The word that
 * matters is **BASE64URL**. Plain base64 differs in exactly three characters — `+` → `-`, `/` → `_`,
 * and the `=` padding is dropped — and a challenge that carries any of the three is not rejected with
 * a message about encoding. The authorization request succeeds, the login page appears, the user types
 * their password, and the *token* request comes back `invalid_grant`, which reads like a bad code, a
 * clock skew, or a redirect-URI mismatch. It is the classic silent failure of this flow, so
 * {@link base64UrlEncode} is one function, used for the challenge, the `state` and the `nonce` alike,
 * and `test/auth/pkce.test.ts` asserts the three characters are absent from a real digest rather than
 * trusting the substitution list below to be complete.
 *
 * NOTHING HERE READS A GLOBAL IT CANNOT BE TOLD ABOUT. `randomBytes` and `sha256` are parameters with
 * WebCrypto defaults, because a test that fakes them proves the plumbing and a test that does not
 * proves the cryptography — and this project wants both, separately. See the test file's header for
 * which assertions run against the real `crypto.subtle` and which do not.
 *
 * WHAT IS DELIBERATELY NOT HERE: any notion of "plain" `code_challenge_method`. `S256` is the only
 * method this app will ever send. `plain` puts the verifier in the authorization request, which
 * defeats the point of PKCE on a device where another app can read the redirect.
 *
 * ⚠️ The pool's discovery document does **not** advertise `code_challenge_methods_supported`. Cognito's
 * document is incomplete on this point — it does support `S256` — but that statement is READ, not
 * verified against this pool, and a sibling workstream is confirming it. If `S256` turns out to be
 * refused, the failure will be an `invalid_request` on the authorization redirect, not anything here.
 */

/**
 * The `unreserved` character set from RFC 3986 §2.3, which is exactly the `code_verifier` alphabet
 * RFC 7636 §4.1 permits: `ALPHA / DIGIT / "-" / "." / "_" / "~"`. 66 characters.
 */
const UNRESERVED = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

/**
 * The largest multiple of 66 that fits in a byte: `3 * 66`. See {@link createCodeVerifier} for why a
 * byte at or above this is discarded rather than folded with `%`.
 */
const REJECTION_CEILING = 198;

/** RFC 7636 §4.1 bounds on `code_verifier` length. */
export const MIN_VERIFIER_LENGTH = 43;
export const MAX_VERIFIER_LENGTH = 128;

/**
 * 64 characters — comfortably inside the legal range, and above the 43-character floor by enough that
 * a future change to the alphabet cannot push it under. 64 unreserved characters is ~387 bits of
 * entropy, which is not the constraint on anything.
 */
export const DEFAULT_VERIFIER_LENGTH = 64;

/** 32 bytes → a 43-character base64url string. Used for `state` and `nonce`. */
export const DEFAULT_TOKEN_BYTES = 32;

export type RandomBytes = (count: number) => Uint8Array;
export type Sha256 = (bytes: Uint8Array) => Promise<Uint8Array>;

/**
 * `crypto.getRandomValues`. Throws rather than falling back to `Math.random`.
 *
 * A silent downgrade to a predictable PRNG for a `state` value is a CSRF hole that no test would
 * notice, so the absence of WebCrypto is a hard stop. In practice it is only absent outside a secure
 * context, where this app has other problems (no service worker, no camera).
 */
export const webCryptoRandomBytes: RandomBytes = (count) => {
  const provider = globalThis.crypto;
  if (provider === undefined || typeof provider.getRandomValues !== 'function') {
    throw new Error(
      'crypto.getRandomValues is unavailable, so no PKCE value can be generated safely. This needs a ' +
        'secure context (https, or localhost during development).',
    );
  }
  return provider.getRandomValues(new Uint8Array(count));
};

/**
 * `crypto.subtle.digest('SHA-256', …)`.
 *
 * ⚠️ `crypto.subtle` is **only present in a secure context**. On plain `http://` at a non-localhost
 * host it is `undefined`, and the resulting `TypeError` would otherwise surface as an unhandled
 * rejection somewhere inside the sign-in click handler. Hence the explicit message.
 */
export const webCryptoSha256: Sha256 = async (bytes) => {
  const subtle = globalThis.crypto?.subtle;
  if (subtle === undefined) {
    throw new Error(
      'crypto.subtle is unavailable, so the PKCE code_challenge cannot be computed. This needs a ' +
        'secure context (https, or localhost during development).',
    );
  }
  // `bytes.slice()` hands `digest` a plain ArrayBuffer of exactly the right length. A `Uint8Array`
  // that is a VIEW onto a larger buffer would otherwise be read in full by some implementations.
  const digest = await subtle.digest('SHA-256', bytes.slice().buffer);
  return new Uint8Array(digest);
};

/**
 * base64url, unpadded — RFC 4648 §5.
 *
 * The `+`/`/`/`=` translation is the whole point of the function; see the file header.
 */
export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** The inverse, for reading a JWT payload. Tolerates missing padding, which JWTs always omit. */
export function base64UrlDecodeToString(value: string): string {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const remainder = padded.length % 4;
  const full = remainder === 0 ? padded : padded + '='.repeat(4 - remainder);
  const binary = atob(full);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new TextDecoder().decode(bytes);
}

/**
 * A fresh `code_verifier`: `length` characters drawn uniformly from the unreserved set.
 *
 * **REJECTION SAMPLING, NOT `%`.** 256 is not a multiple of 66, so `byte % 66` would make the first
 * 58 characters of the alphabet 1.5× likelier than the last 8. That is a small bias and it would
 * never be noticed, which is exactly the argument for not shipping it: a biased verifier is a weaker
 * verifier for no benefit, and the fix is four lines. Bytes at or above {@link REJECTION_CEILING} are
 * discarded and re-drawn.
 */
export function createCodeVerifier(
  length: number = DEFAULT_VERIFIER_LENGTH,
  randomBytes: RandomBytes = webCryptoRandomBytes,
): string {
  if (!Number.isInteger(length) || length < MIN_VERIFIER_LENGTH || length > MAX_VERIFIER_LENGTH) {
    throw new RangeError(
      `code_verifier length must be an integer in [${MIN_VERIFIER_LENGTH}, ${MAX_VERIFIER_LENGTH}]; got ${String(length)}`,
    );
  }
  const chars: string[] = [];
  let guard = 0;
  while (chars.length < length) {
    // A guard rather than a `while (true)`: an injected `randomBytes` that returns only high bytes
    // would otherwise spin forever inside a click handler with no diagnostic at all.
    guard += 1;
    if (guard > 64) {
      throw new Error('random source produced no usable bytes for a code_verifier');
    }
    for (const byte of randomBytes(Math.max(16, length - chars.length))) {
      if (byte >= REJECTION_CEILING) {
        continue;
      }
      chars.push(UNRESERVED.charAt(byte % UNRESERVED.length));
      if (chars.length === length) {
        break;
      }
    }
  }
  return chars.join('');
}

/**
 * `code_challenge` for `code_challenge_method=S256`.
 *
 * The digest length is asserted. A `sha256` implementation that returns the wrong number of bytes —
 * an injected fake, or a polyfill returning the input — would otherwise produce a plausible-looking
 * base64url string, and the test that "the challenge is derived from the verifier" would pass while
 * exercising nothing.
 */
export async function deriveCodeChallenge(
  verifier: string,
  sha256: Sha256 = webCryptoSha256,
): Promise<string> {
  const digest = await sha256(new TextEncoder().encode(verifier));
  if (digest.length !== 32) {
    throw new Error(`SHA-256 must produce 32 bytes; got ${String(digest.length)}`);
  }
  return base64UrlEncode(digest);
}

/**
 * A random, opaque, URL-safe token. Used for `state` and `nonce`, which are the same shape of thing:
 * a value we generate, send, and expect back unchanged.
 */
export function randomUrlToken(
  byteLength: number = DEFAULT_TOKEN_BYTES,
  randomBytes: RandomBytes = webCryptoRandomBytes,
): string {
  if (!Number.isInteger(byteLength) || byteLength < 16) {
    throw new RangeError('a state/nonce needs at least 16 random bytes');
  }
  return base64UrlEncode(randomBytes(byteLength));
}

/** True when `value` contains only characters the unreserved set permits. */
export function isUnreserved(value: string): boolean {
  return /^[A-Za-z0-9\-._~]+$/.test(value);
}

/** True when `value` is unpadded base64url: no `+`, no `/`, no `=`. */
export function isBase64Url(value: string): boolean {
  return /^[A-Za-z0-9_-]+$/.test(value);
}
